import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationService } from '../services/misc/notification.service';
import { IGlobalStateService } from '../services/public/interfaces/global-state.service';
import { Countries } from '../models';
import { ERROR_ACTION_KEY } from '../tokens';

export type ErrorDetail = {
  message: string;
  status: number;
  timestamp: Date;
};

@Injectable()
export class ErrorsInterceptor implements HttpInterceptor {
  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private globalStateService: IGlobalStateService,
    private translateService: TranslateService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.headers.get('Skip-Error-Interceptor') === 'true') {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        console.log(`************************* HttpError *************************`);
        console.log(`${err.message}`);
        console.log(err);
        console.log(`*************************************************************`);

        const errorMessage = this.readErrorMessage(err);
        const detail = errorMessage.replace(/^\[\s*code\s*:\s*-?\d+\s*\]\s*/i, '').trim();
        const actionKey = request.context.get(ERROR_ACTION_KEY);
        const action = actionKey ? this.translate(actionKey) : '';
        const title =
          action && action !== actionKey ? this.translate('errors.action_impossible', { action }) : this.translate('errors.request_failed');
        const description =
          err.status === 0
            ? this.translate('errors.network')
            : this.translate('errors.http_status', {
                status: err.status,
                detail: detail || this.translate('errors.no_detail'),
              });

        this.notificationService.showRequestError(title, description);
        if (
          request.method === 'GET' &&
          Object.values(Countries).some((country) => errorMessage.includes(country)) &&
          ((err?.url ?? '').includes('vouchers') || (err?.url ?? '').includes('cards') || (err?.url ?? '').includes('gift-card'))
        ) {
          void this.router.navigate(['/']);
        }

        this.globalStateService.setLoading(false);

        return throwError(err);
      }),
    );
  }

  private translate(key: string, params?: Record<string, string | number>): string {
    const translated: unknown = this.translateService.instant(key, params);
    return typeof translated === 'string' ? translated : key;
  }

  /** Reads the server message from the response body, or from a plain error raised by another interceptor. */
  private readErrorMessage(err: HttpErrorResponse): string {
    const responseBody: unknown = err.error;
    if (typeof responseBody === 'string') {
      return responseBody;
    }
    if (responseBody && typeof responseBody === 'object' && 'message' in responseBody && typeof responseBody.message === 'string') {
      return responseBody.message;
    }
    const plainErrorMessage: unknown = err instanceof HttpErrorResponse ? undefined : (err as { message?: unknown }).message;
    return typeof plainErrorMessage === 'string' ? plainErrorMessage : '';
  }
}
